import React, { createContext, ReactNode, useContext, useMemo } from "react";
import { FullViewSpinner } from "@webcore/components/core/FullViewSpinner/FullViewSpinner";
import { OpsAppUserContext } from "@webcore/models/coreModels";
import { OpsAppObjectsHostApi, useOpsAppObjectsHostApi } from "@webcore/services/opsapp/useOpsAppObjectsHostApi";
import { OpsAppDocumentsHostApi, useOpsAppDocumentsHostApi } from "@webcore/services/opsapp/useOpsAppDocumentsHostApi";

/**
 * The platform the host injects into ops apps (custom dashboard views): the user context the
 * session runs under, the stateful objects store and the documents store. Each member is handed
 * to view code as an in-scope global of the same name.
 */
export interface OpsAppPlatform {
  /** The authenticated user behind the session, as the server authorized them. */
  userContext: OpsAppUserContext;
  opsAppObjects: OpsAppObjectsHostApi;
  opsAppDocuments: OpsAppDocumentsHostApi;
}

const OpsAppPlatformContext = createContext<OpsAppPlatform | undefined>(undefined);

interface OpsAppPlatformProviderProps {
  // The pipeline configuration whose object type definitions the objects store validates against
  // (the live one, the draft, or a work branch's). The object store itself is team-scoped.
  dataPipelineConfigurationId: string;
  // The user context the host's session was established with: the ops MFE receives it from its
  // token exchange, a same-origin host derives it from the CoreApplicationContext it already holds.
  userContext: OpsAppUserContext;
  children: ReactNode;
}

/**
 * Provides the ops app platform to a rendered custom view. Shared across React apps: the ops MFE
 * is the primary host (each of its view routes mounts one), and any app that renders a custom view
 * same-origin mounts it the same way. Children render only once the platform is fully initialized
 * (the user context is known and the object type definitions have loaded), so view code always
 * runs against a complete scope.
 */
export const OpsAppPlatformProvider: React.FC<OpsAppPlatformProviderProps> = ({ dataPipelineConfigurationId, userContext, children }) => {
  const opsAppObjects = useOpsAppObjectsHostApi(dataPipelineConfigurationId);
  const opsAppDocuments = useOpsAppDocumentsHostApi();
  const platform = useMemo(
    () => (opsAppObjects === null ? null : { userContext, opsAppObjects, opsAppDocuments }),
    [userContext, opsAppObjects, opsAppDocuments]
  );

  if (platform === null) {
    return <FullViewSpinner />;
  }

  return (
    <OpsAppPlatformContext.Provider value={platform}>
      {children}
    </OpsAppPlatformContext.Provider>
  );
};

export const useOpsAppPlatform = (): OpsAppPlatform => {
  const context = useContext(OpsAppPlatformContext);

  if (context === undefined) {
    throw new Error("useOpsAppPlatform must be used within an OpsAppPlatformProvider");
  }

  return context;
};
